import { Disclosure } from '@headlessui/react'
import { CheckIcon } from '@heroicons/react/20/solid'
import { MinusSmallIcon, PlusSmallIcon } from '@heroicons/react/24/outline'
import { useFetcher, useLocation } from '@remix-run/react'
import { useTranslation } from 'react-i18next'
import { Button } from '#app/components/ui/button.tsx'

import { PRICING_PLANS, Currency } from '#app/features/stripe/plans.ts'
import { useCurrentLanguage } from '#app/hooks/use-current-language.tsx'
import { useCustomNavigate } from '#app/hooks/use-custom-navigate.tsx'
import { useOptionalUser } from '#app/utils/user.ts'

function classNames(...classes: any) {
	return classes.filter(Boolean).join(' ')
}

export const PaymentScreen: React.FC<any> = () => {
	const planFetcher = useFetcher()
	const currentRoute = useLocation()
	const currentLanguage = useCurrentLanguage()
	const { t } = useTranslation('payment')
	const optionalUser = useOptionalUser()
	const navigate = useCustomNavigate()

	const faqs = [
		{
			question: t('faqQuestion1'),
			answer: t('faqAnswer1'),
		},
		{
			question: t('faqQuestion2'),
			answer: t('faqAnswer2'),
		},
		{
			question: t('faqQuestion3'),
			answer: t('faqAnswer3'),
		},
		{
			question: t('faqQuestion4'),
			answer: t('faqAnswer4'),
		},
	]

	return (
		<>
			<div className="mx-auto mb-2 w-full">
				<div className="p-4 pb-0">
					<div className="mx-auto max-w-7xl px-6 lg:px-8">
						<div className="mx-auto max-w-4xl text-center">
							<h1 className="text-base font-semibold leading-7 text-primary-400">
								{t('pricingTitle')}
							</h1>
							<p className="mt-2 text-3xl font-bold tracking-tight text-gray-900">
								{t('pricingSubtitle')}
							</p>
						</div>
						<p className="mx-auto mt-6 max-w-2xl text-center text-lg leading-8 text-gray-600">
							{t('pricingDescription')}
						</p>
						<div className="isolate mx-auto mt-10 grid max-w-md grid-cols-1 gap-8 md:max-w-2xl md:grid-cols-2 lg:max-w-4xl xl:mx-0 xl:max-w-none xl:grid-cols-3">
							{Object.values(PRICING_PLANS(t)).map(plan => (
								<div
									key={plan.id}
									className={classNames(
										plan.mostPopular
											? 'shadow-sm ring-2 ring-primary-400'
											: 'ring-1 ring-gray-200',
										'rounded-3xl p-8',
									)}
								>
									<h2
										className={classNames(
											plan.mostPopular ? 'text-primary-400' : 'text-gray-900',
											'text-lg font-semibold leading-8',
										)}
									>
										{plan.name}
									</h2>
									<p className="mt-6 flex items-baseline gap-x-1">
										<span className="text-4xl font-bold tracking-tight text-gray-900">
											€{plan.prices[Currency.EUR] / 100}
										</span>
									</p>
									<div className="mt-6 w-full">
										<Button
											variant={plan.mostPopular ? 'default' : 'secondary'}
											className="w-full"
											onClick={() => {
												if (!optionalUser) {
													navigate({
														route: 'login',
														searchParams: `/${currentLanguage}/chat`,
													})

													return
												}
												const formData = new FormData()
												const priceCurrentPlan = plan.prices[Currency.EUR]

												formData.append('plan', plan.backendId)
												formData.append(
													'redirectUrl',
													`${currentLanguage}/checkout?plan=${plan.backendId}`,
												)
												formData.append('cancelUrl', currentRoute.pathname)
												formData.append('locale', currentLanguage)
												formData.append('productName', plan.name)
												formData.append('price', `${priceCurrentPlan}`)

												planFetcher.submit(formData, {
													method: 'post',
													action: '/create-checkout',
												})
											}}
										>
											{t('buyButton')} {plan.name}
										</Button>
									</div>
									<ul className="mt-8 space-y-3 text-sm leading-6 text-gray-600">
										{plan.features.map(feature => (
											<li key={feature} className="flex gap-x-3">
												<CheckIcon
													className="h-6 w-5 flex-none text-primary-400"
													aria-hidden="true"
												/>
												{feature}
											</li>
										))}
									</ul>
								</div>
							))}
						</div>
					</div>
				</div>
			</div>
			<div className="mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8 lg:py-40">
				<div className="mx-auto max-w-4xl divide-y divide-gray-900/10">
					<h2 className="text-2xl font-bold leading-10 tracking-tight text-gray-900">
						{t('faqTitle')}
					</h2>
					<dl className="mt-10 space-y-6 divide-y divide-gray-900/10">
						{faqs.map(faq => (
							<Disclosure as="div" key={faq.question} className="pt-6">
								{({ open }) => (
									<>
										<dt>
											<Disclosure.Button className="flex w-full items-start justify-between text-left text-gray-900">
												<span className="text-base font-semibold leading-7">
													{faq.question}
												</span>
												<span className="ml-6 flex h-7 items-center">
													{open ? (
														<MinusSmallIcon
															className="h-6 w-6"
															aria-hidden="true"
														/>
													) : (
														<PlusSmallIcon
															className="h-6 w-6"
															aria-hidden="true"
														/>
													)}
												</span>
											</Disclosure.Button>
										</dt>
										<Disclosure.Panel as="dd" className="mt-2 pr-12">
											<p className="text-base leading-7 text-gray-600">
												{faq.answer}
											</p>
										</Disclosure.Panel>
									</>
								)}
							</Disclosure>
						))}
					</dl>
				</div>
			</div>
		</>
	)
}
